import { inject } from "@angular/core";
import {
  HttpErrorResponse,
  type HttpEvent,
  type HttpHandlerFn,
  type HttpRequest,
} from "@angular/common/http";
import { catchError, from, switchMap, throwError, type Observable } from "rxjs";

import { AUTH_PROVIDER } from "../auth/auth-provider.token";
import { ActingOrgService } from "../services/acting-org.service";
import { AuthStateService } from "../services/auth-state.service";

import type { IAuthProvider } from "../auth/interfaces/i-auth-provider";

/**
 * The API error code meaning "this token's customer claim is stale", as opposed
 * to "you are not signed in".
 *
 * Both are 401, and the difference decides whether retrying can possibly work.
 * A plain UNAUTHORIZED means the session is gone and a retry is pointless. This
 * one means the session is fine but the token predates a change to which
 * customer the organization maps to — so a FRESH token fixes it and a cached one
 * never will, because it carries the same stale claim.
 *
 * A list of one, deliberately: an ABSENT claim is not an error on this path.
 * The API only cross-checks a claim the token actually carries, so before the
 * post-login Action ships there is nothing to be stale about.
 */
const STALE_CLAIM_CODES: readonly string[] = ["CUSTOMER_CLAIM_STALE"];

/** True only for a 401 whose body names one of the codes above. */
const isStaleClaim = (error: unknown): boolean => {
  if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
    return false;
  }
  const body: unknown = error.error;
  if (typeof body !== "object" || body === null) {
    return false;
  }
  const code: unknown = (body as { code?: unknown }).code;
  return typeof code === "string" && STALE_CLAIM_CODES.includes(code);
};
/**
 * The identity route, which must never carry `X-Acting-Org`.
 *
 * "Who am I" is not a question about the organization being viewed, and
 * answering it through an acting-org header is what let a stale one lock a user
 * out. `X-Acting-Org` is refused by the API for any token without the Davaco
 * Admin role, and `actingOrg.slug()` is restored from localStorage at
 * construction — so a slug left behind by a previous admin session was attached
 * to the bootstrap /portal/me call (the id-token union still reads as admin),
 * the API answered 403, `MeService.load()` returned null, and the guard's
 * outage fallback trusted the SPA claim and admitted the user into the 403 wall
 * the guard exists to prevent.
 *
 * Excluding the route here rather than clearing the stored slug at bootstrap
 * keeps "view as" surviving a reload, which is the behaviour it is supposed to
 * have, and makes the guard's null branch mean only what it claims: a genuine
 * outage.
 */
const IDENTITY_PATH = "/portal/me";

/**
 * Attaches the bearer token, and the acting-organization header when a Davaco
 * Admin is viewing as a customer.
 *
 * `X-Acting-Org` is a REQUEST, not a grant. The API honours it only for a
 * verified token carrying the Davaco Admin role, and refuses every mutating
 * request while it is set.
 */
export function authInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  // The branding route is anonymous by design and is fetched during bootstrap,
  // before any session exists. Attaching a token would be pointless, and
  // resolving AUTH_PROVIDER for it drags the auth provider into the very
  // request the auth provider depends on.
  if (request.url.includes("/portal/branding/")) {
    return next(request);
  }

  const provider: IAuthProvider = inject(AUTH_PROVIDER);
  const actingOrg = inject(ActingOrgService);
  const authState = inject(AuthStateService);

  /** The request with whatever headers this token and role call for. */
  const authorized = (token: string): HttpRequest<unknown> => {
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };

    const slug: string | null = actingOrg.slug();
    if (slug !== null && authState.isDavacoAdmin() && !request.url.endsWith(IDENTITY_PATH)) {
      headers["X-Acting-Org"] = slug;
    }

    return request.clone({ setHeaders: headers });
  };

  return from(provider.getAccessToken())
.pipe(
    switchMap((token: string | null): Observable<HttpEvent<unknown>> => {
      if (token === null) {
        return next(request);
      }

      return next(authorized(token))
.pipe(
        catchError((error: unknown): Observable<HttpEvent<unknown>> => {
          // ONCE, and only for a stale customer claim. Retrying a plain 401
          // would loop against a genuinely expired session, and retrying any
          // other status would silently double non-idempotent requests.
          if (!isStaleClaim(error)) {
            return throwError((): unknown => error);
          }

          return from(provider.refreshAccessToken())
.pipe(
            switchMap((fresh: string | null): Observable<HttpEvent<unknown>> => {
              // No fresh token means the refresh needs interaction, or the
              // claim is genuinely absent rather than merely stale. Surface
              // the original 401 — its message tells the user to sign in
              // again, which is exactly right.
              if (fresh === null) {
                return throwError((): unknown => error);
              }
              return next(authorized(fresh));
            })
          );
        })
      );
    })
  );
}
